import type {
    BaseSelectionOption,
    SearchableSelectionOption,
} from "app/pages/sales-intelligence/common-components/column-picker";

export const partitionOptionsBySelectedIds = <Option extends BaseSelectionOption>(
    columnOptions: readonly Option[],
    selectedColumnIds: readonly string[],
): [readonly Option[], readonly Option[]] => {
    const selectedOptionsMap = new Map<string, Option>();
    const selectedOptions: Option[] = [];
    const nonSelectedOptions: Option[] = [];

    const selectedIdsSet = new Set(selectedColumnIds);
    for (const option of columnOptions) {
        if (selectedIdsSet.has(option.id)) {
            selectedOptionsMap.set(option.id, option);
        } else {
            nonSelectedOptions.push(option);
        }
    }

    // Build selectedOptions in the order provided by selectedColumnIds
    for (const id of selectedColumnIds) {
        const option = selectedOptionsMap.get(id);

        if (option) {
            selectedOptions.push(option);
        }
    }

    const sortByOrder = (a: Option, b: Option) => {
        if (a.orderIndex != null && b.orderIndex != null) {
            return b.orderIndex - a.orderIndex;
        }

        if (a.orderIndex != null && b.orderIndex == null) {
            return -1;
        }

        if (a.orderIndex == null && b.orderIndex != null) {
            return 1;
        }

        return 0;
    };

    selectedOptions.sort(sortByOrder);

    return [selectedOptions, nonSelectedOptions];
};

/**
 * Narrows the options to the selected category, and — when a search is active — to the matches in
 * `scoresById`, ordered by how well they matched.
 *
 * `scoresById` is null when there is no query, which is a distinct case rather than an empty map:
 * matching is done over the *whole* option set so the search index can cache its documents, so an
 * empty map means "searched and found nothing", not "not searching".
 */
export const filterAndSortByScores = <O extends SearchableSelectionOption>(
    options: readonly O[],
    scoresById: ReadonlyMap<string, number> | null,
    selectedCategory: string,
    areAllCategoriesSelected: boolean,
): readonly O[] => {
    const isInSelectedCategory = (option: O) => areAllCategoriesSelected || option.category === selectedCategory;

    if (!scoresById) {
        return options.filter(isInSelectedCategory);
    }

    return options
        .filter((option) => isInSelectedCategory(option) && scoresById.has(option.id))
        .sort((a, b) => (scoresById.get(a.id) ?? 0) - (scoresById.get(b.id) ?? 0));
};
